import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
  Typography, Grid, Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, Alert, Box,
} from '@mui/material';
import dayjs from 'dayjs';
import { InvTextField, InvSelect } from '../InvField';
import {
  createVendorQuote, addVendorQuoteItem, submitVendorQuote,
} from '../../api/rfq';
import { vendorDisplayLabel } from '../../utils/rfqEntity';
import { calcLine, calcQuoteTotals } from '../../utils/rfqComparison';
import { GST_RATE_OPTIONS } from '../../constants/rfqManagement';
import { formatCurrency, generateTxnNumber } from '../../utils';
import { IPD_COLORS, cardSx } from '../../theme';

function emptyLine(item) {
  return {
    rfq_item_id: item.id,
    item_id: item.item_id,
    uom_id: item.uom_id || '',
    requiredQty: Number(item.required_quantity) || 0,
    qty: String(item.required_quantity ?? ''),
    rate: '',
    discountPercent: '0',
    taxPercent: '0',
    skipped: false,
  };
}

export default function RfqQuoteEntryDialog({
  open,
  rfq,
  rfqItems = [],
  vendorDirectory = [],
  existingQuotes = [],
  resolveItem,
  onClose,
  onSuccess,
}) {
  const [vendorId, setVendorId] = useState('');
  const [quoteNumber, setQuoteNumber] = useState(() => generateTxnNumber('QT'));
  const [quoteDate, setQuoteDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [validUntil, setValidUntil] = useState(dayjs().add(30, 'day').format('YYYY-MM-DD'));
  const [deliveryDays, setDeliveryDays] = useState('7');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState(() => rfqItems.map(emptyLine));
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset the form whenever it reopens on a different RFQ.
  const itemKey = rfqItems.map((i) => i.id).join(',');
  const [seenKey, setSeenKey] = useState(itemKey);
  if (seenKey !== itemKey) {
    setSeenKey(itemKey);
    setLines(rfqItems.map(emptyLine));
  }

  /** Vendors invited to this RFQ who have not already quoted. */
  const vendorOptions = useMemo(() => {
    const quoted = new Set(existingQuotes.map((q) => q.vendor_id));
    const invited = rfq?.invited_vendor_ids || [];
    return vendorDirectory
      .filter((v) => {
        const id = v.vendor_id || v.id;
        if (quoted.has(id)) return false;
        return invited.length === 0 || invited.includes(id);
      })
      .map((v) => ({ value: v.vendor_id || v.id, label: vendorDisplayLabel(v) }));
  }, [vendorDirectory, existingQuotes, rfq]);

  const priced = lines
    .filter((l) => !l.skipped && l.rate !== '' && Number(l.qty) > 0)
    .map((l) => ({
      ...l,
      ...calcLine({
        qty: l.qty, rate: l.rate,
        discountPercent: l.discountPercent, taxPercent: l.taxPercent,
      }),
    }));
  const totals = calcQuoteTotals(priced);

  const isLate = rfq?.submission_deadline
    && dayjs().isAfter(dayjs(rfq.submission_deadline));
  const isPartial = priced.length > 0 && priced.length < rfqItems.length;

  function setLine(index, patch) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  const missingRate = lines.some((l) => !l.skipped && l.rate === '');
  const errors = {
    vendor: submitted && !vendorId ? 'Choose the vendor this quote came from.' : '',
    quoteNumber: submitted && !quoteNumber.trim() ? "Enter the vendor's quote reference." : '',
    lines: submitted && priced.length === 0
      ? 'Price at least one item, or this quote is empty.'
      : (submitted && missingRate ? 'Enter a rate on every item that is not marked "Cannot supply".' : ''),
  };
  const hasErrors = Boolean(errors.vendor || errors.quoteNumber || errors.lines);

  async function handleSave() {
    setSubmitted(true);
    if (!vendorId || !quoteNumber.trim() || priced.length === 0 || missingRate) return;

    setLoading(true);
    setError(null);
    try {
      const quote = await createVendorQuote({
        rfq_id: rfq.id || rfq.rfq_id,
        vendor_id: vendorId,
        quote_number: quoteNumber.trim(),
        quote_date: quoteDate || undefined,
        valid_until: validUntil || undefined,
        payment_terms: paymentTerms.trim() || undefined,
        remarks: remarks.trim() || undefined,
        // The service has no late flag, so the fact is recorded on the quote itself.
        quote_payload: isLate ? { entered_after_deadline: true } : {},
      });
      const quoteId = quote?.id || quote?.vendor_quote_id;
      if (!quoteId) throw new Error('Quote created but no ID was returned');

      // Sequential, not parallel: each add recalculates the quote totals server-side.
      for (const line of priced) {
        await addVendorQuoteItem(quoteId, {
          rfq_item_id: line.rfq_item_id,
          item_id: line.item_id,
          quoted_quantity: String(line.qty),
          uom_id: line.uom_id || undefined,
          unit_rate: String(line.rate),
          discount_percent: String(line.discountPercent || 0),
          tax_percent: String(line.taxPercent || 0),
          delivery_days: Number(deliveryDays) || 0,
        });
      }

      await submitVendorQuote(quoteId);
      onSuccess?.({ quoteId, isPartial });
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to save the quote');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { ...cardSx, p: 0.5 } }}
    >
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>
        Enter a vendor quote
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isLate && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The submission deadline has passed. This quote will be recorded as a late
            submission.
          </Alert>
        )}

        {vendorOptions.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Every invited vendor has already submitted a quote on this RFQ.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <InvSelect
              label="Vendor"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              options={vendorOptions}
              error={Boolean(errors.vendor)}
              helperText={errors.vendor}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvTextField
              label="Vendor's quote reference"
              value={quoteNumber}
              onChange={(e) => setQuoteNumber(e.target.value)}
              error={Boolean(errors.quoteNumber)}
              helperText={errors.quoteNumber}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvTextField label="Quote date" type="date" value={quoteDate}
              onChange={(e) => setQuoteDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvTextField label="Valid until" type="date" value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvTextField label="Delivery days" type="number" inputProps={{ min: 0 }}
              value={deliveryDays} onChange={(e) => setDeliveryDays(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InvTextField label="Payment terms" value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)} />
          </Grid>
        </Grid>

        {errors.lines && <Alert severity="error" sx={{ mb: 1 }}>{errors.lines}</Alert>}

        {isPartial && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            This is a partial quote — {priced.length} of {rfqItems.length} items priced.
            It will be ranked after quotes that cover every item, because a total that
            leaves items out is not comparable.
          </Alert>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 860, '& td, & th': { borderColor: IPD_COLORS.border } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 160 }}>Item</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Required</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Can supply</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Rate</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Disc %</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>GST %</TableCell>
                <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Line total</TableCell>
                <TableCell align="center" sx={{ color: IPD_COLORS.textSecondary }}>Cannot supply</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, i) => {
                const calc = line.skipped ? null : calcLine({
                  qty: line.qty, rate: line.rate,
                  discountPercent: line.discountPercent, taxPercent: line.taxPercent,
                });
                const short = !line.skipped && Number(line.qty) > 0
                  && Number(line.qty) < line.requiredQty;
                return (
                  <TableRow key={line.rfq_item_id} sx={line.skipped ? { opacity: 0.5 } : undefined}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {resolveItem ? resolveItem({ item_id: line.item_id }) : line.item_id}
                    </TableCell>
                    <TableCell align="right">{line.requiredQty}</TableCell>
                    <TableCell align="right">
                      <InvTextField
                        type="number" inputProps={{ min: 0 }} value={line.qty}
                        disabled={line.skipped}
                        onChange={(e) => setLine(i, { qty: e.target.value })}
                      />
                      {short && (
                        <Typography sx={{ fontSize: 10, color: IPD_COLORS.warning.text }}>
                          Part supply
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <InvTextField
                        type="number" inputProps={{ min: 0, step: '0.0001' }} value={line.rate}
                        disabled={line.skipped}
                        onChange={(e) => setLine(i, { rate: e.target.value })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <InvTextField
                        type="number" inputProps={{ min: 0, max: 100 }} value={line.discountPercent}
                        disabled={line.skipped}
                        onChange={(e) => setLine(i, { discountPercent: e.target.value })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <InvSelect
                        value={line.taxPercent}
                        disabled={line.skipped}
                        onChange={(e) => setLine(i, { taxPercent: e.target.value })}
                        options={GST_RATE_OPTIONS}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {line.skipped || line.rate === '' ? '—' : formatCurrency(calc.total)}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={line.skipped}
                        onChange={(e) => setLine(i, { skipped: e.target.checked })}
                        sx={{ color: IPD_COLORS.primary }}
                        inputProps={{ 'aria-label': 'Vendor cannot supply this item' }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={7}>
            <InvTextField label="Remarks" multiline rows={2} value={remarks}
              onChange={(e) => setRemarks(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <Box sx={{ p: 2, borderRadius: '8px', backgroundColor: IPD_COLORS.selectedBg }}>
              {[
                ['Basic', totals.basic],
                ['Discount', totals.discount],
                ['Taxable', totals.taxable],
                ['GST', totals.tax],
              ].map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>{label}</Typography>
                  <Typography sx={{ fontSize: 13 }}>{formatCurrency(value)}</Typography>
                </Box>
              ))}
              <Box sx={{
                display: 'flex', justifyContent: 'space-between',
                borderTop: `1px solid ${IPD_COLORS.border}`, pt: 1, mt: 1,
              }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Net total</Typography>
                <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{formatCurrency(totals.net)}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          {priced.length} of {rfqItems.length} items priced
        </Typography>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || (submitted && hasErrors) || vendorOptions.length === 0}
          variant="contained"
          sx={{ backgroundColor: IPD_COLORS.primary }}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
        >
          Save and submit quote
        </Button>
      </DialogActions>
    </Dialog>
  );
}
